const ENVIRONMENTS = ["test", "prod"] as const;

export type PortalEnvironment = (typeof ENVIRONMENTS)[number];

export type PortalConfig = {
  environment: PortalEnvironment;
  tenantId: string;
  clientId: string;
  apiScope: string;
  apiBase: string;
  redirectUri: string;
  appRegistrationName: `wylo-rc-portal-${PortalEnvironment}`;
};

const ALLOWED = new Set([
  "environment",
  "tenantId",
  "clientId",
  "apiScope",
  "apiBase",
  "redirectUri",
]);

const SECRET_KEYS = new Set([
  "clientSecret",
  "client_secret",
  "AZURE_CLIENT_SECRET",
  "blobConnectionString",
  "sas",
  "accountKey",
]);

export class ConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConfigError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requiredText(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new ConfigError(`${label} is required`);
  }
  return value.trim();
}

export function parseConfig(value: unknown): PortalConfig {
  if (!isRecord(value)) throw new ConfigError("config must be an object");
  for (const key of Object.keys(value)) {
    if (SECRET_KEYS.has(key)) throw new ConfigError("config contains a secret field");
    if (!ALLOWED.has(key)) throw new ConfigError(`unexpected config key ${key}`);
  }
  const environment = requiredText(value.environment, "environment");
  if (environment !== "test" && environment !== "prod") {
    throw new ConfigError("environment must be test or prod");
  }
  const apiBase = requiredText(value.apiBase, "apiBase").replace(/\/$/, "");
  const redirectUri = requiredText(value.redirectUri, "redirectUri");
  if (!/^https?:\/\//.test(apiBase)) throw new ConfigError("apiBase must be an http(s) URL");
  if (!/^https?:\/\//.test(redirectUri)) throw new ConfigError("redirectUri must be an http(s) URL");
  return {
    environment,
    tenantId: requiredText(value.tenantId, "tenantId"),
    clientId: requiredText(value.clientId, "clientId"),
    apiScope: requiredText(value.apiScope, "apiScope"),
    apiBase,
    redirectUri,
    appRegistrationName: `wylo-rc-portal-${environment}`,
  };
}

export function configFromEnv(env: ImportMetaEnv): PortalConfig {
  return parseConfig({
    environment: env.VITE_PORTAL_ENV,
    tenantId: env.VITE_ENTRA_TENANT_ID,
    clientId: env.VITE_ENTRA_CLIENT_ID,
    apiScope: env.VITE_API_SCOPE,
    apiBase: env.VITE_API_BASE,
    redirectUri: env.VITE_REDIRECT_URI,
  });
}

export async function loadConfig(): Promise<PortalConfig> {
  if (import.meta.env.DEV) return configFromEnv(import.meta.env);
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) throw new ConfigError("config.json is missing");
  return parseConfig(await response.json());
}
